
import * as React from 'react';
const { Suspense } = React;
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { LoadingTimeoutHandler } from './LoadingTimeoutHandler';
import LoadingScreen from './LoadingScreen';
import { logger } from '@/utils/logger';

interface LazyRouteWrapperProps {
  children: React.ReactNode;
  routeName: string;
  preloadHints?: string[];
  loadingMessage?: string;
  skeleton?: React.ReactNode;
  timeout?: number;
}

export const LazyRouteWrapper: React.FC<LazyRouteWrapperProps> = ({
  children,
  routeName,
  preloadHints = [],
  loadingMessage,
  skeleton,
  timeout = 15000
}) => {
  const handleTimeout = () => {
    logger.warn(`[LazyRouteWrapper] Route ${routeName} timed out after ${timeout}ms`);
    // Could send analytics event here
  };

  // Routes that deserve the full branded loading experience
  const brandedRoutes = ['Home'];

  const fallbackComponent = skeleton || (
    brandedRoutes.includes(routeName) ? (
      <LoadingScreen 
        message={loadingMessage || "Welcome to your spiritual journey..."}
      />
    ) : (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          🧪 Loading {routeName} route...
        </div>
      </div>
    )
  );

  // Preload hints for performance
  React.useEffect(() => {
    if (preloadHints.length > 0) {
      preloadHints.forEach(hint => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = hint;
        link.as = 'script';
        document.head.appendChild(link);
      });
    }
  }, [preloadHints, routeName]);

  return (
    <LoadingTimeoutHandler timeout={timeout} onTimeout={handleTimeout}>
      <RouteErrorBoundary routeName={routeName} preloadHints={preloadHints}>
        <Suspense fallback={fallbackComponent}>
          {children}
        </Suspense>
      </RouteErrorBoundary>
    </LoadingTimeoutHandler>
  );
};
